import { useAggregationsContext, useCallbackRef, useMarketAccountsContext, useMarketsContext } from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { getMarketSelectorAggregationLabel } from '../../MarketSelector/presets/getMarketSelectorAggregationLabel';
import { useFavoriteAggregationPresets } from '../../MarketSelector/presets/useFavoriteAggregationPresets';
import { useMarketSelectorAggregationPresets } from '../../MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorItem, MarketSelectorValue } from '../../MarketSelector/types';
import { getSelectionsFromPreset } from '../../MarketSelector/useMarketSelector';
import {
  persistMarketSelectorValueFromSelections,
  useMarketSelectorDarkSelectionGuards,
  type PersistMarketDataCardMarketSelection,
} from '../useMarketDataCardMarketSelector';

interface UseMarketDataCardQuickMarketSelectorControlsParams {
  isOpen: boolean;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  selectedAggregation: string | undefined;
  marketSelectorItems: MarketSelectorItem[];
  onPersistMarketSelection: (update: PersistMarketDataCardMarketSelection) => void;
}

/**
 * Lightweight market-selector state for the market card header controls
 * (market account button and aggregation prev/next arrows).
 * Does not build per-symbol selector inventory or run the full MarketSelector hook.
 */
export function useMarketDataCardQuickMarketSelectorControls({
  isOpen,
  selectedMarkets,
  selectedMarketAccounts,
  selectedAggregation,
  marketSelectorItems,
  onPersistMarketSelection,
}: UseMarketDataCardQuickMarketSelectorControlsParams) {
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { getOnlyDarkOrNoDarkSelections, isLatestSelectionDark } = useMarketSelectorDarkSelectionGuards();
  const { aggregationsByName } = useAggregationsContext();
  const { aggregationPresetsByID } = useMarketSelectorAggregationPresets();

  const itemsMap = useMemo(() => new Map(marketSelectorItems.map(item => [item.name, item])), [marketSelectorItems]);
  const favoriteAggregationPresets = useFavoriteAggregationPresets(itemsMap);

  const selectedMarketOrAccountNames = useMemo(
    () => [...selectedMarkets, ...selectedMarketAccounts],
    [selectedMarketAccounts, selectedMarkets]
  );

  const selectedAggregationLabel = useMemo(
    () => getMarketSelectorAggregationLabel(selectedAggregation, aggregationPresetsByID, aggregationsByName),
    [aggregationPresetsByID, aggregationsByName, selectedAggregation]
  );

  const persistMarketSelectorValue = useCallbackRef((value: MarketSelectorValue) => {
    persistMarketSelectorValueFromSelections({
      value,
      marketsByName,
      marketAccountsByName,
      getOnlyDarkOrNoDarkSelections,
      isLatestSelectionDark,
      onPersistMarketSelection,
    });
  });

  const selectAggregationPreset = useCallback(
    (aggregationID: string) => {
      const preset = aggregationPresetsByID?.get(aggregationID);
      if (!preset) {
        return;
      }

      const selections = getSelectionsFromPreset(preset, itemsMap).map(item => item.name);

      persistMarketSelectorValue({
        type: 'preset',
        presetID: aggregationID,
        selections,
      });
    },
    [aggregationPresetsByID, itemsMap, persistMarketSelectorValue]
  );

  return useMemo(
    () => ({
      isOpen,
      selectedMarketOrAccountNames,
      selectedAggregationID: selectedAggregation,
      selectedAggregationLabel,
      favoriteAggregationPresets,
      selectAggregationPreset,
    }),
    [
      favoriteAggregationPresets,
      isOpen,
      selectAggregationPreset,
      selectedAggregation,
      selectedAggregationLabel,
      selectedMarketOrAccountNames,
    ]
  );
}
